import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { getTowerHelper } from '../utils/towerHelpers';

// Context type definition
interface JokeInputContextType {
  showJokeInput: (towerId: string) => void;
  hideJokeInput: () => void;
  isVisible: boolean;
  currentTowerId: string | null;
}

// Create context with default values
const JokeInputContext = createContext<JokeInputContextType>({
  showJokeInput: () => {},
  hideJokeInput: () => {},
  isVisible: false,
  currentTowerId: null
});

// Hook to use the joke input context
export const useJokeInput = () => useContext(JokeInputContext);

// Props for the provider component
interface JokeInputProviderProps {
  children: ReactNode;
}

// Provider component
export const JokeInputProvider: React.FC<JokeInputProviderProps> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTowerId, setCurrentTowerId] = useState<string | null>(null);
  const [jokeText, setJokeText] = useState('');
  const { isMobile } = useResponsive();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  const showJokeInput = useCallback((towerId: string) => {
    console.log("Show joke input for tower ID:", towerId);
    setCurrentTowerId(towerId);
    setJokeText('');
    setIsVisible(true);
  }, []);
  
  const hideJokeInput = useCallback(() => {
    setIsVisible(false);
    setCurrentTowerId(null);
    setJokeText('');
  }, []);
  
  const handleSubmit = useCallback(() => {
    if (currentTowerId && jokeText.trim()) {
      console.log("Submitting joke for tower ID:", currentTowerId, "Joke:", jokeText.trim());
      
      try {
        const helper = getTowerHelper(currentTowerId);
        
        if (helper && helper.handleJokeSubmit) {
          // Call the handler
          helper.handleJokeSubmit(jokeText.trim());
          console.log("Joke submitted successfully!");
          
          // Show success message
          setShowSuccessMessage(true);
          
          // Add a slight delay before hiding to ensure the effect has time to register
          setTimeout(() => {
            setShowSuccessMessage(false);
            hideJokeInput();
          }, 1500);
        } else {
          console.error("No joke submit handler found for tower ID:", currentTowerId);
          alert("Error: Could not submit joke. Please try again.");
          hideJokeInput();
        }
      } catch (error) {
        console.error("Error submitting joke:", error);
        alert("Error submitting joke. Please try again.");
        hideJokeInput();
      }
    } else {
      hideJokeInput();
    }
  }, [currentTowerId, jokeText, hideJokeInput]);
  
  return (
    <JokeInputContext.Provider value={{
      showJokeInput,
      hideJokeInput,
      isVisible,
      currentTowerId
    }}>
      {children}
      
      {isVisible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '10px' : '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: isMobile ? '15px' : '20px',
            width: isMobile ? '90%' : '60%',
            maxWidth: '800px'
          }}>
            {showSuccessMessage ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px'
              }}>
                <h2 style={{ 
                  color: '#4ae04a',
                  fontSize: isMobile ? '20px' : '28px',
                  marginBottom: '15px'
                }}>
                  Joke Submitted Successfully!
                </h2>
                <p style={{
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#333'
                }}>
                  Your tower has been upgraded!
                </p>
              </div>
            ) : (
              <>
                <h2 style={{ 
                  textAlign: 'center', 
                  color: '#e04a9c',
                  fontSize: isMobile ? '18px' : '24px',
                  marginBottom: '15px'
                }}>
                  Tell a joke to make the aliens laugh!
                </h2>
                
                <textarea
                  value={jokeText}
                  onChange={(e) => setJokeText(e.target.value)}
                  placeholder="Type your joke here..."
                  style={{
                    width: '100%',
                    height: isMobile ? '120px' : '150px',
                    padding: '10px',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    marginBottom: '15px',
                    fontSize: isMobile ? '14px' : '16px',
                    resize: 'none'
                  }}
                />
                
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button 
                    onClick={hideJokeInput}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#e04a4a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: isMobile ? '14px' : '16px'
                    }}
                  >
                    Cancel
                  </button>
                  
                  <button 
                    onClick={handleSubmit}
                    disabled={!jokeText.trim()}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: jokeText.trim() ? '#e04a9c' : '#cccccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: jokeText.trim() ? 'pointer' : 'not-allowed',
                      fontSize: isMobile ? '14px' : '16px'
                    }}
                  >
                    Submit Joke
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </JokeInputContext.Provider>
  );
}; 